import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, XCircle } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

interface KsaReadinessBannersProps {
  isKsa: boolean;
  blockingCount: number;
  warningCount: number;
  companyChecksFailed: boolean;
  companyFailCount: number;
}

/** Only surfaces when action is needed, so a red or amber state leads the page instead of getting lost. */
export const KsaReadinessBanners: React.FC<KsaReadinessBannersProps> = ({
  isKsa,
  blockingCount,
  warningCount,
  companyChecksFailed,
  companyFailCount,
}) => {
  const { t } = useTranslation();
  const scrollToIssues = () => {
    document.getElementById('employee-issues')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <>
      {(blockingCount > 0 || companyChecksFailed) && (
        <div
          className="rounded-xl border-2 border-red-300 bg-red-50 p-4 flex items-start justify-between gap-4 shadow-sm"
          data-testid="wps-blocking-banner"
        >
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-red-100 p-2 shrink-0">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="font-semibold text-red-800">
                {t('payroll.wps.blockedTitle', '{{n}} issue(s) must be fixed before you can generate the {{file}} file', {
                  n: blockingCount + companyFailCount,
                  file: isKsa ? 'Mudad' : 'SIF',
                })}
              </p>
              <p className="text-sm text-red-700 mt-0.5">
                {companyChecksFailed && blockingCount === 0
                  ? t('payroll.wps.blockedSubtitleCompany', 'Company-level setup is incomplete. Scroll down to Company Setup Pre-Validation to fix.')
                  : t('payroll.wps.blockedSubtitle', 'Generation stays disabled until every employee passes the pre-flight checks below.')}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="border-red-300 text-red-700 hover:bg-red-100 shrink-0"
            onClick={scrollToIssues}
            iconName="ArrowDown"
          >
            {t('payroll.wps.reviewIssues', 'Review issues')}
          </Button>
        </div>
      )}
      {blockingCount === 0 && !companyChecksFailed && warningCount > 0 && (
        <div
          className="rounded-xl border border-amber-300 bg-amber-50 p-3 flex items-center gap-3"
          data-testid="wps-warning-banner"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800">
            {t('payroll.wps.warnBanner', 'Ready to generate. {{n}} coverage warning(s) to resolve before the bank deadline.', {
              n: warningCount,
            })}
          </p>
        </div>
      )}
    </>
  );
};
